import { NgModule, Optional, SkipSelf, ErrorHandler, OnDestroy } from '@angular/core';
import * as moment from 'moment';
import 'moment/min/locales';
import { Settings } from './settings';
import { UrlBuilder } from './url-builder/url-builder';
import { LanguagesStore } from './languages-store';
import { LicenseManager } from 'ag-grid-enterprise/main';
import { ActivityLogger } from '../activities/activity-logger';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { GlobalErrorHandler } from './global-error-handler';
import { NoneValueHelper } from './none-value-helper';
import { filter, map, mergeMap } from 'rxjs/operators';
import { Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { NavigationActivityResolver } from '../activities/navigation-activity/navigation-activity-resolver';
import { NavigationActivity } from '../activities/navigation-activity/navigation-activity';

@NgModule({
    providers: [
        UrlBuilder,
        LanguagesStore,
        ActivityLogger,
        NavigationActivity,
        NavigationActivityResolver,
        NoneValueHelper,
        { provide: ErrorHandler, useClass: GlobalErrorHandler }
    ]
})
export class CoreModule implements OnDestroy {

    private readonly subscription = new Subscription();

    private readonly navigationEnd$ = this.router.events
        .pipe(
            filter<NavigationEnd>(event => event instanceof NavigationEnd)
        );

    constructor(
        @Optional() @SkipSelf() coreModule: CoreModule,
        private activityLogger: ActivityLogger,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private title: Title,
        private settings: Settings) {
        if (coreModule) {
            throw new Error('CoreModule is already loaded');
        }

        this.initializeLocale();
        this.setupAgGrid();
        this.setupNavigationActivityLogger();
        this.setupTitleChanger();
        this.setupUserStartEmulationActivityLogger();
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    private initializeLocale() {
        const browserNavigator = window.navigator as BrowserNavigator;
        const lang = browserNavigator.languages ? browserNavigator.languages[0] : (browserNavigator.language || browserNavigator.userLanguage); // http://stackoverflow.com/a/25603630
        moment.locale(lang);
    }

    private setupAgGrid() {
        LicenseManager.setLicenseKey('CompanyName=EVOTEK, Inc_on_behalf_of_Covance Inc,LicensedGroup=Xcellerate,LicenseType=MultipleApplications,LicensedConcurrentDeveloperCount=25,LicensedProductionInstancesCount=6,AssetReference=AG-014122,ExpiryDate=29_April_2022_[v2]_MTY1MTE4NjgwMDAwMA==10b7827b3979c91da5ece12f2d7ebf07');
    }

    private setupNavigationActivityLogger() {
        this.navigationEnd$.subscribe(() => {
                this.activityLogger.logNavigationActivity();
            });
    }

    private setupTitleChanger(): void {
        this.subscription.add(this.navigationEnd$
            .pipe(
                map(() => this.activatedRoute),
                map(route => {
                    while (route.firstChild && !route.snapshot.data['title']) {
                        route = route.firstChild;
                    }

                    return route;
                }),
                filter(route => route.outlet === 'primary'),
                mergeMap(route => route.data),
                map(d => d['title'] || this.title.getTitle())
            )
            .subscribe(stateTitle => {
                this.title.setTitle(stateTitle);
            }));
    }

    private setupUserStartEmulationActivityLogger() {
        if (this.settings.emulatedUserId) {
            this.activityLogger.logUserEmulationActivity('StartEmulation', this.settings.emulatedUserId);
        }
    }
}

interface BrowserNavigator extends Navigator {
    languages: string[];
    userLanguage: string;
}
